/**
 * Canonical URL mapping and utilities
 * Ensures consistent canonical URLs across the site
 */

// Route to canonical URL mapping
export const CANONICAL_URL_MAP: Record<string, string> = {
  // Main pages
  "/": "https://tigola.com",
  "/ride": "https://tigola.com/ride",
  "/drive": "https://tigola.com/drive",
  "/business": "https://tigola.com/business",
  "/about": "https://tigola.com/about",
  "/contact": "https://tigola.com/contact",
  "/help": "https://tigola.com/help",
  "/safety": "https://tigola.com/safety",
  "/blog": "https://tigola.com/blog",

  // Service pages
  "/airports": "https://tigola.com/airports",
  "/taxi": "https://tigola.com/taxi",
  "/coverage": "https://tigola.com/coverage",
  "/popular-routes": "https://tigola.com/popular-routes",
  "/reserve": "https://tigola.com/reserve",
  "/app": "https://tigola.com/app",
  "/wedding-transport": "https://tigola.com/wedding-transport",
  "/event-transport": "https://tigola.com/event-transport",
  "/airport-transfers": "https://tigola.com/airport-transfers",

  // Location pages
  "/leicester": "https://tigola.com/taxi/leicester",
  "/birmingham": "https://tigola.com/taxi/birmingham",
  "/locations": "https://tigola.com/locations",
  "/cities": "https://tigola.com/taxi",

  // Legal pages
  "/terms": "https://tigola.com/terms",
  "/privacy": "https://tigola.com/privacy",
  "/cookies": "https://tigola.com/cookies",

  // Company pages
  "/careers": "https://tigola.com/careers",
  "/press": "https://tigola.com/press",

  // Redirected routes (these should redirect to canonical)
  "/executive": "https://tigola.com/business",
  "/corporate-travel": "https://tigola.com/business",
  "/birmingham-city": "https://tigola.com/taxi/birmingham",
  "/group-travel": "https://tigola.com/business",
  "/home": "https://tigola.com",
};

/**
 * Generate canonical URL for a given path
 */
export function getCanonicalUrl(path: string): string {
  // Normalize the path
  const normalizedPath = normalizePath(path);

  // Check if we have a specific mapping
  if (CANONICAL_URL_MAP[normalizedPath]) {
    return CANONICAL_URL_MAP[normalizedPath];
  }

  // Handle dynamic routes
  if (normalizedPath.startsWith("/airports/")) {
    const airportCode = normalizedPath.split("/")[2];
    return `https://tigola.com/airports/${airportCode.toLowerCase()}`;
  }

  if (normalizedPath.startsWith("/taxi/")) {
    const cityName = normalizedPath.split("/")[2];
    return `https://tigola.com/taxi/${cityName.toLowerCase()}`;
  }

  if (normalizedPath.startsWith("/taxis/")) {
    const townName = normalizedPath.split("/")[2];
    return `https://tigola.com/taxis/${townName.toLowerCase()}`;
  }

  if (normalizedPath.startsWith("/cities/")) {
    const cityName = normalizedPath.split("/")[2];
    return `https://tigola.com/taxi/${cityName.toLowerCase()}`;
  }

  // Default fallback
  return `https://tigola.com${normalizedPath}`;
}

/**
 * Normalize a path for canonical URL generation
 */
export function normalizePath(path: string): string {
  // Remove query parameters and hash
  const cleanPath = path.split("?")[0].split("#")[0];

  // Convert to lowercase
  let normalized = cleanPath.toLowerCase();

  // Remove trailing slash except for root
  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }

  // Ensure it starts with /
  if (!normalized.startsWith("/")) {
    normalized = "/" + normalized;
  }

  return normalized;
}

/**
 * Check if a canonical URL is properly formatted
 */
export function normalizeCanonicalUrl(url: string): string {
  const parsed = new URL(url);

  if (parsed.protocol !== "https:") {
    throw new Error("Canonical URLs must use HTTPS.");
  }

  if (!parsed.hostname.endsWith("tigola.com")) {
    throw new Error("Canonical URLs must use the tigola.com domain.");
  }

  const normalizedPath = normalizePath(parsed.pathname);

  if (normalizedPath === "/") {
    return "https://tigola.com";
  }

  return `https://tigola.com${normalizedPath}`;
}

export function validateCanonicalUrl(url: string): boolean {
  try {
    return normalizeCanonicalUrl(url) === url;
  } catch {
    return false;
  }
}

/**
 * Get the current page's canonical URL based on window.location
 */
export function getCurrentCanonicalUrl(): string {
  if (typeof window === "undefined") {
    return "https://tigola.com";
  }

  return getCanonicalUrl(window.location.pathname);
}

/**
 * Ensure all pages have proper canonical URLs set
 */
export function ensureCanonicalUrl(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const currentCanonical = getCurrentCanonicalUrl();

  // Check if canonical link exists
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

  // Only set if no page-managed canonical exists
  if (!link || link.getAttribute("data-managed-by") !== "page-seo") {
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("data-managed-by", "fallback");
      document.head.appendChild(link);
    }

    // Only update if not managed by page SEO
    if (link.getAttribute("data-managed-by") !== "page-seo") {
      link.setAttribute("href", currentCanonical);
      link.setAttribute("data-managed-by", "fallback");
    }
  }
}

export default {
  getCanonicalUrl,
  normalizePath,
  normalizeCanonicalUrl,
  validateCanonicalUrl,
  getCurrentCanonicalUrl,
  ensureCanonicalUrl,
  CANONICAL_URL_MAP,
};
